// ─── PER-USER PAGE / SECTION VISIBILITY ──────────────────────────────
// Central definition of the admin-portal sections whose visibility can be
// controlled per user via `user_profiles.page_permissions`.
//
// Semantics of profile.page_permissions:
//   • null / undefined → no per-user restriction (user sees everything their
//     ROLE allows — the original behavior; fully backwards compatible).
//   • an array of keys  → explicit allowlist. The user only sees the sections
//     whose key is in the array. This can only ever *subtract* from what the
//     role already permits — it can never grant access a role wouldn't have.
//
// Notes:
//   • super_admin is NEVER restricted (protects the account that manages this).
//   • `settings` is intentionally NOT controllable here — it stays HQ-admin-only
//     by role, so it never appears in a franchise user's togglable list.
//   • franchiseOnly / adminOnly mirror the sidebar so the editor only offers a
//     page to the roles that could actually reach it.

export const CONTROLLABLE_PAGES = [
  { key: 'dashboard',   label: 'Dashboard',     group: 'Overview' },
  { key: 'fleet',       label: 'Fleet',         group: 'Overview' },
  { key: 'hq-fleet',    label: 'Peacock Fleet', group: 'Overview',        franchiseOnly: true },
  { key: 'onboarding',  label: 'Onboarding',    group: 'Operations' },
  { key: 'rentals',     label: 'Rentals',       group: 'Operations' },
  { key: 'calendar',    label: 'Calendar',      group: 'Operations' },
  { key: 'turo',        label: 'Turo Inbox',    group: 'Operations' },
  { key: 'live-map',    label: 'Live Map',      group: 'Operations',      adminOnly: true },
  { key: 'owners',      label: 'Owners',        group: 'Partners & Money' },
  { key: 'partners',    label: 'Partners',      group: 'Partners & Money' },
  { key: 'broker-availability', label: 'Broker Availability', group: 'Partners & Money' },
  { key: 'payouts',     label: 'Payouts',       group: 'Partners & Money' },
  { key: 'reports',     label: 'Reports',       group: 'Partners & Money' },
  { key: 'renters',     label: 'Renters',       group: 'Manage' },
  { key: 'maintenance', label: 'Maintenance',   group: 'Manage' },
  { key: 'users',       label: 'Team / Users',  group: 'Manage',          franchiseOnly: true },
]

export const CONTROLLABLE_KEYS = CONTROLLABLE_PAGES.map(p => p.key)

// Map a first path segment to its controllable page key.
const SEGMENT_TO_KEY = {
  'fleet': 'fleet',
  'fleet-catalog': 'fleet',
  'hq-fleet': 'hq-fleet',
  'onboarding': 'onboarding',
  'bookings': 'onboarding',
  'rentals': 'rentals',
  'calendar': 'calendar',
  'turo': 'turo',
  'live-map': 'live-map',
  'owners': 'owners',
  'partners': 'partners',
  'broker-availability': 'broker-availability',
  'payouts': 'payouts',
  'reports': 'reports',
  'renters': 'renters',
  'maintenance': 'maintenance',
  'users': 'users',
}

// Resolve a pathname to its controllable page key, or null if the route isn't
// one of the gated sections (e.g. /settings, unknown routes → not gated here).
export function pathToPageKey(pathname) {
  if (!pathname || pathname === '/' ) return 'dashboard'
  const seg = pathname.split('/').filter(Boolean)[0]
  if (!seg) return 'dashboard'
  return SEGMENT_TO_KEY[seg] || null
}

// Which roles use the admin shell and are therefore affected by page_permissions.
export function usesAdminShell(role) {
  return role === 'admin' || role === 'super_admin' || role === 'franchise_admin'
}

// Does the ROLE (ignoring per-user perms) allow this controllable page at all?
export function roleAllowsPage(role, page) {
  const isAdminRole = role === 'admin' || role === 'super_admin'
  const isFranchise = role === 'franchise_admin'
  if (page.franchiseOnly) return isFranchise
  if (page.adminOnly) return isAdminRole
  return true
}

// The pages a given role is allowed to toggle in the editor (role-bounded set).
export function pagesForRole(role) {
  return CONTROLLABLE_PAGES.filter(p => roleAllowsPage(role, p))
}

// Is a profile allowed to see a given controllable page key?
//   super_admin → always; null perms → yes; else must be in the allowlist.
export function isPageAllowed(profile, key) {
  if (!key) return true                       // ungated route
  if (!profile) return false
  if (profile.role === 'super_admin') return true
  const perms = profile.page_permissions
  if (perms == null) return true              // no per-user restriction
  return Array.isArray(perms) && perms.includes(key)
}
